import { Navigate, NavLink, Outlet, useParams } from 'react-router-dom';
import { useSafe } from '../../context/SafeContext';
import LoadingPane from '../../components/LoadingPane';

export function OrgDetailLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const { orgs, orgsLoaded, pendingFinalizations } = useSafe();
  const org = orgs.find((o) => o.id === orgId);
  const isAdmin = org?.role === 'admin';
  const manageNeedsAttention = pendingFinalizations.some((f) => f.orgId === orgId);

  if (!org) {
    // `orgs` is emptied on lock and refetched after unlock, so an absent org
    // means "still loading" until orgsLoaded says otherwise.
    if (!orgsLoaded) {
      return <LoadingPane label="Loading family…" className="px-6 py-6 text-hbs-text-mute" />;
    }
    return <div className="px-6 py-6 text-hbs-text-mute">Family not found.</div>;
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-3 text-sm border-b-2 transition-colors ${
      isActive
        ? 'border-hbs-primary text-hbs-text font-medium'
        : 'border-transparent text-hbs-text-mute hover:text-hbs-text'
    }`;

  return (
    <div className="p-9 max-w-3xl mx-auto">
      <header className="mb-5">
        <NavLink to="/orgs" className="text-xs text-hbs-text-mute hover:text-hbs-text">← Families</NavLink>
        <h1 className="font-display font-semibold text-[32px] tracking-hbs-tight text-hbs-text mt-1">{org.name}</h1>
      </header>

      {/* Every tab here is admin-only, so a member gets no tab strip at all
          rather than an empty bordered row. Overview and Usage are deliberately
          absent: both pages still exist and are reachable by URL
          (`/orgs/<id>/overview`, `/orgs/<id>/usage`), they're just no longer
          advertised. A member landing on the family goes straight to Overview
          (see OrgDefaultRoute), which is the whole page for them. */}
      {isAdmin && (
        <nav className="flex gap-2 border-b border-hbs-border mb-4">
          <NavLink to={`/orgs/${orgId}/manage`} className={tabClass}>
            <span className="inline-flex items-center gap-1.5">
              Manage
              {manageNeedsAttention && (
                <span
                  aria-label="The Manage tab needs your attention"
                  className="inline-block w-2 h-2 rounded-full bg-hbs-danger"
                />
              )}
            </span>
          </NavLink>
          {/* Route stays /categories — renaming it would break existing links and
              bookmarks; only the label is the user-facing surface. */}
          <NavLink to={`/orgs/${orgId}/categories`} className={tabClass}>Binders</NavLink>
          <NavLink to={`/orgs/${orgId}/circles`} className={tabClass}>Circles</NavLink>
        </nav>
      )}

      <Outlet />
    </div>
  );
}

/**
 * Index route for `/orgs/:orgId`. Admins land on Manage — the family's default
 * page — and members on Overview, which is the only one of the two they can
 * open. Rendered inside OrgDetailLayout, which has already resolved the org
 * (and shown the loading/not-found states), so an absent org here just means
 * the parent is about to render one of those instead.
 *
 * A redirect rather than rendering the tab in place, so the address bar names
 * the page you're on and a reload keeps you there.
 */
export function OrgDefaultRoute() {
  const { orgId } = useParams<{ orgId: string }>();
  const { orgs } = useSafe();
  const org = orgs.find((o) => o.id === orgId);
  if (!org) return null;
  return <Navigate to={org.role === 'admin' ? 'manage' : 'overview'} replace />;
}
